/**
 * Оформление и личные цвета.
 *
 * Сначала выбирается оформление — готовый набор значений, — а поверх него
 * человек правит отдельные цвета. Цвет вещь личная: кому-то нужен контраст
 * повыше, кто-то работает при ярком свете, а кто-то просто не любит бежевый.
 * Настройка живёт в профиле человека, а не в контуре: сосед за соседним
 * столом видит своё.
 */

import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";

import { updatePreferences } from "@/shared/api/auth";
import {
  COLOR_SLOTS,
  SKINS,
  getColors,
  getSkin,
  setColors,
  setSkin,
  type ColorSlot,
  type Colors,
  type Skin,
} from "@/shared/lib/theme";
import { Button } from "@/shared/ui/Button";
import { SegmentedControl } from "@/shared/ui/Choice";
import { useToast } from "@/shared/ui/Toast";

/** Что показать в поле, пока человек ничего не выбрал: текущий цвет темы.
 *
 * Токены записаны в OKLCH, а `input[type=color]` понимает только
 * шестнадцатеричную запись. Пересчитывает холст: он умеет принять любую
 * форму записи цвета и вернуть готовые байты. */
function current(token: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  if (/^#[0-9a-fA-F]{6}$/.test(value)) {
    return value;
  }
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (context === null || value === "") {
    return "#ffffff";
  }
  context.fillStyle = value;
  context.fillRect(0, 0, 1, 1);
  const [red, green, blue] = context.getImageData(0, 0, 1, 1).data;
  return "#" + [red, green, blue].map((part) => (part ?? 0).toString(16).padStart(2, "0")).join("");
}

export function Palette(): React.ReactElement {
  const toast = useToast();
  const [colors, setLocal] = React.useState<Colors>(() => getColors());
  const [skin, setSkinState] = React.useState<Skin>(() => getSkin());

  const save = useMutation({
    mutationFn: (next: { colors?: Colors; skin?: Skin }) => updatePreferences(next),
    onError: (error: Error) =>
      toast.show({ title: "Настройка не сохранилась", description: error.message, tone: "danger" }),
  });

  /**
   * Отправка отстаёт от ползунка.
   *
   * `input[type=color]` шлёт событие непрерывно, пока тянут ползунок: без
   * задержки на одно движение уходили десятки запросов, а из-за того, что
   * ответы приходят вразнобой, в профиле мог осесть промежуточный цвет.
   */
  const pending = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(pending.current), []);

  function change(slot: ColorSlot, value: string): void {
    const next = { ...colors, [slot]: value };
    setLocal(next);
    setColors(next);
    window.clearTimeout(pending.current);
    pending.current = window.setTimeout(() => save.mutate({ colors: next }), 400);
  }

  /**
   * Смена оформления сбрасывает личные цвета.
   *
   * Личный цвет — поправка к набору значений: он подбирался к прежнему
   * оформлению и поверх нового держался бы как заплата, из-за которой
   * переключение выглядело бы несработавшим.
   */
  function chooseSkin(next: Skin): void {
    window.clearTimeout(pending.current);
    setSkinState(next);
    setSkin(next);
    const hadColors = Object.keys(colors).length > 0;
    setLocal({});
    setColors({});
    save.mutate({ skin: next, colors: {} });
    if (hadColors) {
      toast.show({
        title: "Оформление изменено",
        description: "Личные цвета сброшены: они подбирались к прежнему.",
      });
    }
  }

  function reset(): void {
    window.clearTimeout(pending.current);
    setLocal({});
    setColors({});
    save.mutate({ colors: {} });
    toast.show({ title: "Цвета вернулись к стандартным" });
  }

  return (
    <div className="flex w-[300px] flex-col gap-[var(--pt-space-8)] p-[var(--pt-space-8)]">
      <div className="flex flex-col gap-[var(--pt-space-4)]">
        <span className="text-[var(--pt-fs-xs)] font-medium text-[var(--pt-text-secondary)]">
          Оформление
        </span>
        <SegmentedControl<Skin>
          label="Оформление"
          value={skin}
          onValueChange={chooseSkin}
          options={SKINS.map((item) => ({ value: item.value, label: item.label }))}
        />
      </div>

      <p className="text-[var(--pt-fs-xs)] text-[var(--pt-text-secondary)]">
        Цвета сохраняются в вашем профиле: за другой машиной вы увидите то же самое, а коллеги —
        своё.
      </p>

      {COLOR_SLOTS.map((slot) => (
        <label
          key={slot.key}
          className="flex items-center justify-between gap-[var(--pt-space-8)] text-[var(--pt-font-ui)]"
        >
          <span>{slot.label}</span>
          <input
            type="color"
            aria-label={slot.label}
            value={colors[slot.key] ?? current(slot.token)}
            onChange={(event) => change(slot.key, event.target.value)}
            className="h-[24px] w-[44px] cursor-pointer rounded-[var(--pt-radius-3)] border border-[var(--pt-border-control)] bg-transparent p-0"
          />
        </label>
      ))}

      <Button
        variant="secondary"
        size="sm"
        icon={<RotateCcw className="size-[var(--pt-icon)]" />}
        onClick={reset}
      >
        Вернуть стандартные
      </Button>
    </div>
  );
}
